import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useMe, canUseTool } from "../lib/me";
import CompanySwitcher from "../tools/collection/components/CompanySwitcher";
import BusinessSwitcher from "../tools/outreach/components/BusinessSwitcher";
import { isMasterScope } from "../tools/outreach/lib/business";

// Outreach shows the Master library or one company's pages, never both.
const OUTREACH_MASTER_NAV = [
  { to: "/outreach/research",  label: "Research" },
  { to: "/outreach/roles",     label: "Roles" },
  { to: "/outreach/campaigns", label: "Templates" },
  { to: "/outreach/senders",   label: "Senders" },
  { to: "/outreach/import",    label: "Import" },
];
const OUTREACH_COMPANY_NAV = [
  { to: "/outreach",            label: "Dashboard" },
  { to: "/outreach/contacts",   label: "Contacts" },
  { to: "/outreach/review",     label: "Quick review" },
  { to: "/outreach/logs",       label: "Email log" },
  { to: "/outreach/verify-log", label: "Verification log" },
];

interface Props { onLogout: () => void }

interface ToolEntry {
  key: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  ported: boolean;          // whether the tool exists in the monorepo at all
  badge?: string;
  subnav?: { to: string; label: string }[];
}

const TOOLS: ToolEntry[] = [
  {
    key: "collection",
    label: "Collection",
    path: "/collection",
    ported: true,
    icon: <IconCash />,
    subnav: [
      { to: "/collection/dashboard", label: "Dashboard" },
      { to: "/collection/clients",   label: "Clients" },
      { to: "/collection/import",    label: "Import" },
      { to: "/collection/review",    label: "Review" },
      { to: "/collection/templates", label: "Templates" },
      { to: "/collection/send",      label: "Send" },
      { to: "/collection/log",       label: "Send log" },
    ],
  },
  {
    key: "payroll",
    label: "Payroll",
    path: "/payroll",
    ported: true,
    icon: <IconUsers />,
    subnav: [
      { to: "/payroll",            label: "Dashboard" },
      { to: "/payroll/employees",  label: "Employees" },
      { to: "/payroll/periods",    label: "Payroll runs" },
      { to: "/payroll/slips",      label: "Salary slips" },
      { to: "/payroll/reports",    label: "Reports" },
      { to: "/payroll/import",     label: "Import" },
      { to: "/payroll/settings",   label: "Settings" },
    ],
  },
  {
    key: "wht",
    label: "WHT Monitor",
    path: "/wht",
    ported: true,
    icon: <IconReceipt />,
    subnav: [
      { to: "/wht",            label: "Dashboard" },
      { to: "/wht/clients",    label: "Clients" },
      { to: "/wht/import",     label: "Import" },
      { to: "/wht/email",      label: "Email" },
      { to: "/wht/templates",  label: "Templates" },
      { to: "/wht/reports",    label: "Reports" },
    ],
  },
  { key: "monday",  label: "Contacts",    path: "/monday",  ported: true,                  icon: <IconBoard /> },
  {
    key: "outreach",
    label: "Outreach",
    path: "/outreach",
    ported: true,
    icon: <IconSend />,
    subnav: OUTREACH_COMPANY_NAV,
  },
];

export default function Layout({ onLogout }: Props) {
  const loc = useLocation();
  const me = useMe();
  const onCollection = loc.pathname.startsWith("/collection");
  const onMonday = loc.pathname.startsWith("/monday");
  const isAdmin = me?.role === "admin";

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem("mb_sidebar_collapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("mb_sidebar_collapsed", collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  // Count of invoices awaiting review, shown as a badge on the collection "Review" tab.
  // Refetches on navigation and when the Review page fires "pc-review-changed" after resolving one.
  const canCollection = canUseTool(me, "collection");
  const [reviewCount, setReviewCount] = useState(0);
  useEffect(() => {
    if (!canCollection) { setReviewCount(0); return; }
    let cancelled = false;
    const load = () => {
      const raw = localStorage.getItem("pc_company_id");
      const cid = raw ? Number(raw) : null;
      if (!cid) { setReviewCount(0); return; }
      api<{ estimates: unknown[] }>(`/api/collection/estimates?company_id=${cid}&review=1`)
        .then((r) => { if (!cancelled) setReviewCount(r.estimates.length); })
        .catch(() => { /* ignore — badge is non-critical */ });
    };
    load();
    window.addEventListener("pc-review-changed", load);
    return () => { cancelled = true; window.removeEventListener("pc-review-changed", load); };
  }, [canCollection, loc.pathname]);

  async function logout() {
    await api("/api/auth/logout", { method: "POST" });
    onLogout();
  }

  const railWidth = collapsed ? "w-14" : "w-60";

  return (
    <div className="flex h-screen">
      {/* SIDEBAR */}
      <aside className={`sticky top-0 flex h-screen ${railWidth} shrink-0 flex-col border-r border-slate-200/70 bg-white/85 backdrop-blur-md transition-[width] duration-150`}>
        <div className={`flex items-center gap-2 py-4 ${collapsed ? "flex-col px-2" : "px-3"}`}>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-slate-800 to-slate-950 text-[12px] font-bold text-white shadow-soft">M</div>
          {!collapsed && (
            <div className="min-w-0 truncate text-[14px] font-semibold tracking-tightish text-slate-900">
              MailBIT <span className="text-slate-400">·</span> <span className="font-normal text-slate-500">Tools</span>
            </div>
          )}
          <button
            onClick={() => setCollapsed(v => !v)}
            className={`${collapsed ? "mt-1" : "ml-auto"} !h-7 !w-7 !p-0 text-slate-500 hover:bg-slate-100`}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <IconChevronRight /> : <IconChevronLeft />}
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-2">
          <NavLink to="/" end className={navItemClass} title={collapsed ? "Home" : undefined}>
            <IconHome />{!collapsed && <span>Home</span>}
          </NavLink>

          {!collapsed && (
            <div className="px-2.5 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Tools</div>
          )}

          {TOOLS.map((t) => {
            const canUse = t.ported && canUseTool(me, t.key);
            const active = loc.pathname.startsWith(t.path);
            return (
              <div key={t.key}>
                {canUse ? (
                  <NavLink
                    to={t.path}
                    title={collapsed ? t.label : undefined}
                    className={`flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium transition ${
                      active ? "bg-indigo-50 text-indigo-700" : "text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    {t.icon}{!collapsed && <span>{t.label}</span>}
                  </NavLink>
                ) : (
                  <div
                    className="flex cursor-not-allowed items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium text-slate-400"
                    title={!t.ported ? "Not yet ported into the monorepo" : "Your account doesn't have access to this tool"}
                  >
                    {t.icon}{!collapsed && <span>{t.label}</span>}
                    {!collapsed && t.badge && <span className="ml-auto rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">{t.badge}</span>}
                    {!collapsed && t.ported && !canUseTool(me, t.key) && <span className="ml-auto text-[10px] text-slate-400">no access</span>}
                  </div>
                )}
                {!collapsed && active && canUse && t.subnav && (
                  <div className="ml-6 mt-1 space-y-0.5 border-l border-slate-200 pl-3">
                    {(t.key === "outreach" ? (isMasterScope() ? OUTREACH_MASTER_NAV : OUTREACH_COMPANY_NAV) : t.subnav).map((s) => {
                      const badge = s.to === "/collection/review" && reviewCount > 0 ? reviewCount : null;
                      return (
                        <NavLink key={s.to} to={s.to}
                          className={({ isActive }) =>
                            `flex items-center justify-between gap-2 rounded-md px-2 py-1 text-[13px] transition ${
                              isActive ? "font-medium text-indigo-700" : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                          <span>{s.label}</span>
                          {badge != null && (
                            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-200">
                              {badge}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {isAdmin && (
            <>
              {!collapsed && (
                <div className="px-2.5 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Administration</div>
              )}
              <NavLink to="/admin" className={navItemClass} title={collapsed ? "Users" : undefined}>
                <IconShield />{!collapsed && <span>Users</span>}
              </NavLink>
            </>
          )}
        </nav>

        <div className="border-t border-slate-200/70 p-3">
          {!collapsed && (
            <div className="mb-2 px-1 text-xs">
              <div className="truncate font-medium text-slate-700">{me?.name || me?.email || "—"}</div>
              <div className="text-[11px] text-slate-500">{me?.role === "admin" ? "Admin" : "Member"}</div>
            </div>
          )}
          <button onClick={logout} className="btn-ghost w-full justify-start" title={collapsed ? "Sign out" : undefined}>
            <IconSignout />{!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* MAIN AREA */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-14 items-center gap-4 border-b border-slate-200/70 bg-white/70 px-6 backdrop-blur-md">
          {onCollection ? <CompanySwitcher />
            : loc.pathname.startsWith("/outreach") ? <BusinessSwitcher />
            : <div className="text-sm text-slate-400">MailBIT internal tools</div>}
        </header>
        {onMonday ? (
          // Contacts (Monday) renders inside the main panel with its own internal scroll.
          <main className="flex-1 min-h-0 overflow-hidden">
            <Outlet />
          </main>
        ) : (
          <main className="flex-1 overflow-y-auto px-8 py-7">
            <div className="mx-auto max-w-[1400px]">
              <Outlet />
            </div>
          </main>
        )}
      </div>
    </div>
  );
}

function navItemClass({ isActive }: { isActive: boolean }): string {
  return `flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium transition ${
    isActive ? "bg-slate-900 text-white shadow-soft" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
  }`;
}

/* -------------- Inline icons -------------- */
function IconHome() {
  return <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M10.707 2.293a1 1 0 00-1.414 0L1.293 10.293A1 1 0 002 12h1v6a1 1 0 001 1h3v-5h4v5h3a1 1 0 001-1v-6h1a1 1 0 00.707-1.707l-8-8z" /></svg>;
}
function IconCash() {
  return <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="2.5" y="5" width="15" height="10" rx="1.5"/><circle cx="10" cy="10" r="2"/><path d="M5.5 10h.01M14.5 10h.01"/></svg>;
}
function IconUsers() {
  return <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="7" cy="7" r="3"/><circle cx="14" cy="8" r="2"/><path d="M2 16c0-2.5 2.2-4.5 5-4.5s5 2 5 4.5M12 14c0-1.5 1.5-3 3-3s3 1.5 3 3"/></svg>;
}
function IconReceipt() {
  return <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M5 2.5h10v15l-2.5-1.5L10 17l-2.5-1L5 17.5z"/><path d="M8 6.5h4M8 10h4M8 13h2"/></svg>;
}
function IconBoard() {
  return <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="2.5" y="2.5" width="15" height="15" rx="1.5"/><path d="M2.5 7.5h15M7 7.5v10M12.5 7.5v10"/></svg>;
}
function IconSend() {
  return <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 17l14-7L3 3v5l9 2-9 2v5z"/></svg>;
}
function IconShield() {
  return <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M10 2.5l6 2v5c0 4-2.7 7-6 8-3.3-1-6-4-6-8v-5l6-2z"/></svg>;
}
function IconSignout() {
  return <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 4H4v12h8M16 10H8M13 7l3 3-3 3"/></svg>;
}
function IconChevronLeft() {
  return <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 4.293a1 1 0 010 1.414L8.414 10l4.293 4.293a1 1 0 11-1.414 1.414L6 10.707a1 1 0 010-1.414L11.293 4.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>;
}
function IconChevronRight() {
  return <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.293 4.293a1 1 0 011.414 0L14 9.586a1 1 0 010 1.414L8.707 16.293a1 1 0 11-1.414-1.414L11.586 10 7.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>;
}
